import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { fetchStudentById } from "../../store/studentsSlice";
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Spin,
  Alert,
  Empty,
  Typography,
} from "antd";

const { Title } = Typography;

// small table for assessments
const AssessmentTable = ({ assessments }) => (
  <Table
    size="small"
    pagination={false}
    bordered
    columns={[
      { title: "Name", dataIndex: "name", key: "name" },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (t) => <Tag color={t === "Summative" ? "red" : "blue"}>{t}</Tag>,
      },
      { title: "Points", dataIndex: "points", key: "points" },
      { title: "Achieved", dataIndex: "achieved", key: "achieved" },
    ]}
    dataSource={assessments.map((a) => ({ key: a.id, ...a }))}
  />
);

const StudentReport = () => {
  const dispatch = useDispatch();
  const studentsById = useSelector((s) => s.students.byId);
  const loadingGlobal = useSelector((s) => s.students.loading);

  const [studentIds, setStudentIds] = useState([]);
  const [reports, setReports] = useState([]);
  const [error, setError] = useState(null);

  // ─── 1) Bootstrap effect: read IDs & dispatch fetchStudentById ─────────
  useEffect(() => {
    const login = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const ids = Array.isArray(login.studentIds) ? login.studentIds : [];
    if (ids.length === 0) {
      setStudentIds([]);  // will show global Empty
      return;
    }
    setStudentIds(ids);

    // fire once for each
    ids.forEach((id) => dispatch(fetchStudentById(id)));
  }, [dispatch]);

  // ─── 2) When (and only when) ALL studentsById entries are present ──────
  useEffect(() => {
    if (studentIds.length === 0) {
      setReports([]); // no IDs → no reports
      return;
    }

    // check if every ID has loaded
    const allLoaded = studentIds.every((id) => !!studentsById[id]);
    if (!allLoaded) return; // not yet, wait

    // now fetch all marklists exactly once
    (async () => {
      try {
        const login = JSON.parse(localStorage.getItem("userInfo") || "{}");
        const fetched = await Promise.all(
          studentIds.map(async (rawId) => {
            const stu = studentsById[rawId];
            const idno = stu.studentIdno;
            const name =
              (stu.basicPersonalInformation?.firstName || "") +
              " " +
              (stu.basicPersonalInformation?.surName || "");

            const { data } = await axios.get(
              `${API_BASE_URL}/api/students-marklist-details/student/${idno}`,
              {
                headers: { Authorization: `Bearer ${login.token}` },
              }
            );
            return { idno, name, detail: data.detail };
          })
        );
        setReports(fetched);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load reports");
      }
    })();
  }, [studentIds, studentsById]);

  if (loadingGlobal) return <Spin size="large" style={{ margin: 48 }} />;
  if (error)         return <Alert type="error" message={error} style={{ margin: 24 }} />;
  if (reports.length === 0)
    return <Empty description="No student reports to display" style={{ margin: 48 }} />;

  return (
    <div style={{ padding: 24 }}>
      {reports.map(({ idno, name, detail }) => (
        <Card
          key={idno}
          title={<Title level={4}>{name} — ID: {idno}</Title>}
          style={{ marginBottom: 32 }}
          bordered={false}
        >
          <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Batch">{detail.batch}</Descriptions.Item>
            <Descriptions.Item label="Section">{detail.section}</Descriptions.Item>
            <Descriptions.Item label="Current Term">
              {detail.currentTerm}
            </Descriptions.Item>
          </Descriptions>

          {(!detail.records || detail.records.length === 0) ? (
            <Empty description="No term records available" />
          ) : (
            detail.records.map((rec) => {
              const rows = rec.marks.map((m, i) => ({
                key: i,
                subject: m.subject,
                teacher: m.teacher,
                grade: m.grade,
                total: m.assessments.reduce((s, a) => s + a.points, 0),
                achieved: m.assessments.reduce((s, a) => s + a.achieved, 0),
                assessments: m.assessments,
              }));

              return (
                <div key={rec.term} style={{ marginTop: 32 }}>
                  <Title level={5}>{rec.term}</Title>
                  <Table
                    bordered
                    pagination={false}
                    columns={[
                      { title: "Subject", dataIndex: "subject", key: "subject" },
                      { title: "Teacher", dataIndex: "teacher", key: "teacher" },
                      {
                        title: "Grade",
                        dataIndex: "grade",
                        key: "grade",
                        render: (g) => <Tag color="purple">{g}</Tag>,
                      },
                      { title: "Pts", dataIndex: "total", key: "total" },
                      { title: "Achvd", dataIndex: "achieved", key: "achieved" },
                    ]}
                    dataSource={rows}
                    expandable={{
                      expandedRowRender: (r) => (
                        <AssessmentTable assessments={r.assessments} />
                      ),
                      rowExpandable: (r) =>
                        Array.isArray(r.assessments) && r.assessments.length > 0,
                    }}
                  />
                </div>
              );
            })
          )}
        </Card>
      ))}
    </div>
  );
};

export default StudentReport;
